import { useRef, useCallback } from "react";
import { getShareInfo, ShareInfo } from "@/services/mediaShareService";

const DEFAULT_POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 150; // 5 minutes max at default interval

export const SLOW_POLL_INTERVAL_MS = 30000;

export type PollResult =
  | { type: "ready"; info: ShareInfo }
  | { type: "failed"; error: string }
  | { type: "creating"; info: ShareInfo }
  | { type: "expired" }
  | { type: "timeout" }
  | { type: "error"; error: string };

interface UseSharePollingOptions {
  onResult: (result: PollResult) => void;
  intervalMs?: number;
}

/**
 * Hook for polling share status until ready, failed, or timeout.
 * Used by both MediaShareModal (admin) and ShareDownloadPage (public).
 */
export function useSharePolling({ onResult, intervalMs = DEFAULT_POLL_INTERVAL_MS }: UseSharePollingOptions) {
  const pollCountRef = useRef(0);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (token: string) => {
      try {
        pollCountRef.current += 1;

        if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
          stopPolling();
          onResult({ type: "timeout" });
          return;
        }

        const info = await getShareInfo(token);

        if (info.status === "ready") {
          stopPolling();
          onResult({ type: "ready", info });
        } else if (info.status === "failed") {
          stopPolling();
          onResult({
            type: "failed",
            error: info.error || "Failed to create download package",
          });
        } else if (info.status === "creating") {
          onResult({ type: "creating", info });
          pollTimeoutRef.current = setTimeout(() => poll(token), intervalMs);
        } else if (info.status === "expired" || info.status === "deleted") {
          stopPolling();
          onResult({ type: "expired" });
        }
      } catch (err: unknown) {
        stopPolling();
        const axiosError = err as { response?: { status?: number }; message?: string };
        if (axiosError.response?.status === 404) {
          onResult({ type: "expired" });
        } else {
          onResult({
            type: "error",
            error: axiosError.message || "Failed to check status",
          });
        }
      }
    },
    [onResult, stopPolling, intervalMs]
  );

  const startPolling = useCallback(
    (token: string) => {
      pollCountRef.current = 0;
      pollTimeoutRef.current = setTimeout(() => poll(token), intervalMs);
    },
    [poll, intervalMs]
  );

  const resetPolling = useCallback(() => {
    stopPolling();
    pollCountRef.current = 0;
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    resetPolling,
  };
}
